import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft } from "lucide-react";
import { APP_NAME } from "@/App";

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

    // Update page title
    useEffect(() => {
        document.title = `Privacy Policy - ${APP_NAME}`;
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] pb-8">
            {/* Navigation */}
            <nav className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto border-b border-[#005f6b]/20 backdrop-blur-sm">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
                    <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-full" />
                    <span className="text-xl font-semibold text-[#d4af37]">BackOffice</span>
                </div>
                <div className="flex gap-3">
                    <Button
                        data-testid="pricing-login-btn"
                        variant="ghost"
                        onClick={() => navigate("/login")}
                        className="text-[#f8f8f8] hover:text-[#00ced1] hover:bg-[#005f6b]/20"
                    >
                        Login
                    </Button>
                </div>
            </nav>

            <div className="max-w-6xl mx-auto px-6 py-20">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-bold text-[#d4af37] mb-4">Privacy Policy</h1>
                    <p className="text-[#f8f8f8]/70 text-lg">BackOffice Assistant</p>
                    <p className="text-[#f8f8f8]/50 mt-2">Last updated: December 2025</p>
                </div>

                {/* Content Container */}
                <div className="bg-[#0a1a1f]/40 backdrop-blur-sm rounded-2xl border border-[#005f6b]/30 p-8 md:p-12 shadow-2xl">
                    <div className="prose prose-invert max-w-none">

                        {/* Section 1 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">1. Introduction</h2>
                            <p className="text-[#f8f8f8]/80 leading-relaxed">
                                BackOffice Assistant ("we," "our," or "us") respects your privacy and is committed to protecting your personal information.
                                This Privacy Policy explains how we collect, use, store, and protect your data when you use our web application,
                                including any related services, features, or integrations.
                            </p>
                            <p className="text-[#f8f8f8]/80 leading-relaxed mt-3">
                                By using BackOffice Assistant, you agree to the collection and use of information in accordance with this Privacy Policy.
                            </p>
                        </div>

                        {/* Section 2 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">2. Information We Collect</h2>
                            <p className="text-[#f8f8f8]/80 leading-relaxed mb-4">We may collect the following types of information:</p>

                            <div className="ml-4">
                                <h3 className="text-xl font-semibold text-[#d4af37] mb-2">a. Personal Information</h3>
                                <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                    <li>Name</li>
                                    <li>Email address</li>
                                    <li>Business information</li>
                                    <li>Client or service-related details you voluntarily provide</li>
                                </ul>

                                <h3 className="text-xl font-semibold text-[#d4af37] mb-2 mt-4">b. Calendar Information (Google Calendar Integration)</h3>
                                <p className="text-[#f8f8f8]/80 mb-2">If you choose to connect your Google Calendar, we may access:</p>
                                <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                    <li>Calendar events</li>
                                    <li>Appointment dates and times</li>
                                    <li>Event-related metadata</li>
                                </ul>
                                <p className="text-[#f8f8f8]/80 mt-2">
                                    This access is used only to support scheduling, job tracking, and service coordination within the app.
                                </p>

                                <h3 className="text-xl font-semibold text-[#d4af37] mb-2 mt-4">c. Payment Information</h3>
                                <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                    <li>Payments are processed securely through Stripe.</li>
                                    <li>We do not store or process your credit card information directly.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 3 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">3. How We Use Your Information</h2>
                            <p className="text-[#f8f8f8]/80 mb-3">We use your information to:</p>
                            <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                <li>Provide and operate the BackOffice Assistant platform</li>
                                <li>Generate service sheets and job records</li>
                                <li>Sync appointments with your calendar (if enabled)</li>
                                <li>Improve app functionality and performance</li>
                                <li>Communicate important updates or support-related messages</li>
                            </ul>
                            <p className="text-[#f8f8f8]/80 mt-3 font-semibold">
                                We do not sell, rent, or trade your personal data.
                            </p>
                        </div>

                        {/* Section 4 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">4. Google Calendar API Use Disclosure</h2>
                            <p className="text-[#f8f8f8]/80 mb-3">
                                BackOffice Assistant's use of Google Calendar data complies with the Google API Services User Data Policy,
                                including the Limited Use requirements.
                            </p>
                            <p className="text-[#f8f8f8]/80 mb-2">Specifically:</p>
                            <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                <li>We only access Google Calendar data with your explicit consent</li>
                                <li>We use this data solely to provide scheduling and service-related features</li>
                                <li>We do not use Google Calendar data for advertising</li>
                                <li>We do not share Google Calendar data with third parties</li>
                                <li>You may revoke calendar access at any time through your Google account settings.</li>
                            </ul>
                        </div>

                        {/* Section 5 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">5. Data Security</h2>
                            <p className="text-[#f8f8f8]/80 mb-3">We take reasonable measures to protect your information, including:</p>
                            <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                <li>Secure servers</li>
                                <li>Access controls</li>
                                <li>Industry-standard security practices</li>
                            </ul>
                            <p className="text-[#f8f8f8]/80 mt-3">
                                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security
                                but strive to protect your data responsibly.
                            </p>
                        </div>

                        {/* Section 6 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">6. Data Retention</h2>
                            <p className="text-[#f8f8f8]/80">
                                We retain your data only for as long as necessary to provide our services or comply with legal obligations.
                                You may request deletion of your data at any time.
                            </p>
                        </div>

                        {/* Section 7 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">7. Your Rights</h2>
                            <p className="text-[#f8f8f8]/80 mb-3">You have the right to:</p>
                            <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                <li>Access your personal data</li>
                                <li>Request corrections</li>
                                <li>Request deletion</li>
                                <li>Withdraw consent for integrations (such as Google Calendar)</li>
                            </ul>
                            <p className="text-[#f8f8f8]/80 mt-3">
                                To exercise these rights, please contact us at:{" "}
                                <a href="mailto:lifeacademymastermind@gmail.com" className="text-[#d4af37] hover:text-[#00ced1] underline">
                                    lifeacademymastermind@gmail.com
                                </a>
                            </p>
                        </div>

                        {/* Section 8 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">8. Third-Party Services</h2>
                            <p className="text-[#f8f8f8]/80 mb-3">BackOffice Assistant uses trusted third-party services such as:</p>
                            <ul className="list-disc list-inside text-[#f8f8f8]/80 space-y-1 ml-4">
                                <li>Google (Calendar integration)</li>
                                <li>Stripe (payment processing)</li>
                            </ul>
                            <p className="text-[#f8f8f8]/80 mt-3">
                                Each third party operates under its own privacy policy.
                            </p>
                        </div>

                        {/* Section 9 */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">9. Changes to This Policy</h2>
                            <p className="text-[#f8f8f8]/80">
                                We may update this Privacy Policy from time to time. Any changes will be reflected on this page with
                                an updated revision date.
                            </p>
                        </div>

                        {/* Section 10 */}
                        <div>
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-4">10. Contact Us</h2>
                            <p className="text-[#f8f8f8]/80">
                                If you have any questions about this Privacy Policy, please contact us at:{" "}
                                <a href="mailto:lifeacademymastermind@gmail.com" className="text-[#d4af37] hover:text-[#00ced1] underline">
                                    lifeacademymastermind@gmail.com
                                </a>
                            </p>
                        </div>

                    </div>
                </div>

                {/* Back Button */}
                <div className="mt-8 text-center">
                    <Button
                        onClick={() => navigate("/")}
                        className="bg-[#d4af37] hover:bg-[#b8941f] text-[#0a1a1f] font-semibold px-6 py-3"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Home
                    </Button>
                </div>
            </div>

            <footer className="text-center text-[#f8f8f8]/70 mt-12 text-sm">
                <p>© 2025 BackOffice. All rights reserved. <a className="cursor-pointer text-[#d4af37] hover:text-[#00ced1]" onClick={() => navigate("/privacy-policy")}>Privacy Policy</a></p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
